// src/views/parent/ParentLayout.jsx
import { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useAxios from "../../utils/useAxios";
import { tenantURL } from "../../utils/config";
import Swal from "sweetalert2";
import { ParentDrawerProvider, useParentDrawer } from "../../context/ParentDrawerContext";
import ParentDrawer from "../../components/ParentDrawer";

// Pages Parent
import PAAccueil from "./PAAccueil";
import PABulletins from "./PABulletins";
import PAJustifications from "./PAJustifications";
import PANouvelleJustification from "./PANouvelleJustification";
import PAMessages from "./PAMessages";
import PANotifications from "./PANotifications";
import PAAbonnements from "./PAAbonnements";
import PAOffres from "./PAOffres";
import PAProfil from "./PAProfil";
import PAAide from "./PAAide";
import PAContact from "./PAContact";
import PAEntreesSorties from "./PAEntreesSorties";
import PAStatistiques from "./PAStatistiques";
import PABulletinDetail from "./PABulletinDetail";
import PASituationFrais from "./PASituationFrais";

// ─── MODAL CONFIRMATION DÉCONNEXION ───
function LogoutDialog({ onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="modal-box" style={{ maxWidth: 340 }}>
        <div className="modal-title">
          Déconnexion
          <button className="ibtn" style={{ border: "none" }} onClick={onCancel}>
            <i className="ti ti-x" />
          </button>
        </div>
        <div style={{ fontSize: 15, color: "var(--m)", lineHeight: 1.5, marginBottom: 24 }}>
          Voulez-vous vraiment vous déconnecter ?
        </div>
        <div className="factions">
          <button className="bsec" onClick={onCancel}>Annuler</button>
          <button className="bprim rd" onClick={onConfirm}>Déconnecter</button>
        </div>
      </div>
    </div>
  );
}

// ─── BOTTOM SHEET SÉLECTION ENFANT ───
function EnfantSelectionSheet({ enfants, onSelect, onClose }) {
  return (
    <div className="modal-overlay" style={{ alignItems: "flex-end" }} onClick={onClose}>
      <div
        className="modal-box"
        style={{
          maxWidth: 430,
          width: "100%",
          borderRadius: "20px 20px 0 0",
          maxHeight: "70dvh",
          animation: "slideUp 0.28s ease",
          padding: 0,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: "1px solid var(--b)",
        }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: "var(--pr)" }}>
            Sélectionnez un enfant
          </span>
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        <div style={{ overflowY: "auto" }}>
          {enfants.map(enfant => {
            const classeNom = enfant.inscriptions?.[0]?.classe_nom ?? "";
            const initiale = (enfant.prenom?.[0] ?? "?").toUpperCase();
            return (
              <button
                key={enfant.id}
                onClick={() => onSelect(enfant.id)}
                style={{
                  width: "100%", display: "flex", alignItems: "center",
                  gap: 14, padding: "14px 20px",
                  background: "none", border: "none",
                  borderBottom: "1px solid var(--b)",
                  cursor: "pointer", textAlign: "left",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                <div style={{
                  width: 42, height: 42, borderRadius: "50%", flexShrink: 0,
                  background: "var(--pr-bg)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 16, color: "var(--pr)",
                }}>{initiale}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 15, color: "var(--t)" }}>
                    {enfant.prenom} {enfant.nom}
                  </div>
                  {classeNom && (
                    <div style={{ fontSize: 12, color: "var(--m)", marginTop: 2 }}>
                      Classe : {classeNom}
                    </div>
                  )}
                </div>
                <i className="ti ti-chevron-right" style={{ color: "var(--m)" }} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ─────────────────────────────────────────────
export default function ParentLayout() {
  return (
    <ParentDrawerProvider>
      <ParentLayoutContent />
    </ParentDrawerProvider>
  );
}

// ─── CONTENU DU LAYOUT ───────────────────────────────────────────────
function ParentLayoutContent() {
  const { user, logoutUser } = useAuth();
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useAxios();
  const { closeDrawer } = useParentDrawer();

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showEnfantSheet, setShowEnfantSheet] = useState(false);
  const [enfants, setEnfants] = useState([]);
  const [badges, setBadges] = useState({ messages: 0, notifications: 0 });
  const [username, setUsername] = useState("Parent");
  const [dataError, setDataError] = useState(null);

  const getParentPath = (path) => {
    if (!slug) return "/login";
    return `/${slug}/parent/${path}`;
  };

  // ─── CHARGEMENT DES DONNÉES ────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!slug) return;

    try {
      const [userRes, enfantsRes, messagesRes, notifsRes] = await Promise.all([
        api.get(tenantURL(slug, 'mon-compte/')).catch(() => ({ data: { username: "Parent" } })),
        api.get(tenantURL(slug, 'mon-espace/')),
        api.get(tenantURL(slug, 'message/messages/non-lus/')).catch(() => ({ data: { count: 0 } })),
        api.get(tenantURL(slug, 'notification/notifications/non-lues/')).catch(() => ({ data: { count: 0 } })),
      ]);

      setUsername(userRes.data?.username || "Parent");
      setEnfants(enfantsRes.data || []);
      setBadges({
        messages: messagesRes.data?.count ?? 0,
        notifications: notifsRes.data?.count ?? 0,
      });
      setDataError(null);
    } catch (error) {

      if (!navigator.onLine) {
        setDataError("Pas de connexion Internet.");
      } else if (error.response?.status >= 500) {
        setDataError("Le serveur est momentanément indisponible.");
      } else {
        setDataError("Impossible de charger vos données.");
      }
    }
  }, [slug]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // ─── GESTIONNAIRES ──────────────────────────────────────────────────
  const handleJustifications = () => {
    // 1) Erreur de chargement (hors ligne, serveur down…)
    if (dataError) {
      Swal.fire({
        icon: "error",
        title: "Connexion impossible",
        text: "Impossible de charger la liste de vos enfants. Vérifiez votre connexion Internet.",
        confirmButtonText: "Réessayer",
        confirmButtonColor: "#1B4F80",
      }).then((result) => {
        if (result.isConfirmed) {
          fetchData();
        }
      });
      return;
    }

    // 2) Vraiment aucun enfant
    if (enfants.length === 0) {
      Swal.fire({
        icon: "info",
        title: "Aucun enfant",
        text: "Aucun enfant n'est associé à votre compte. Contactez l'administration.",
        confirmButtonColor: "#1B4F80",
      });
      return;
    }

    // 3) Cas normal
    if (enfants.length === 1) {
      navigate(getParentPath(`justifications/${enfants[0].id}`));
      closeDrawer();
    } else {
      setShowEnfantSheet(true);
    }
  };

  const handleEnfantSelect = (id) => {
    setShowEnfantSheet(false);
    navigate(getParentPath(`justifications/${id}`));
    closeDrawer();
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logoutUser();
  };

  if (!slug) {
    return (
      <div className="loader-container">
        <div style={{ textAlign: "center", color: "var(--m)" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏫</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Chargement...</div>
          <div style={{ fontSize: 14 }}>Aucun établissement trouvé</div>
        </div>
      </div>
    );
  }

  // ─── RENDU ──────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>

      <ParentDrawer
        badges={badges}
        getParentPath={getParentPath}
        onJustificationsClick={handleJustifications}
        onLogoutClick={() => setShowLogoutConfirm(true)}
      />

      {showLogoutConfirm && (
        <LogoutDialog onConfirm={handleLogout} onCancel={() => setShowLogoutConfirm(false)} />
      )}
      {showEnfantSheet && (
        <EnfantSelectionSheet
          enfants={enfants}
          onSelect={handleEnfantSelect}
          onClose={() => setShowEnfantSheet(false)}
        />
      )}

      <div className="app">
        <header className="parent-topbar" />

        <main className="main">
          <div className="content">
            <Routes>
              <Route index element={<Navigate to="accueil" replace />} />

              <Route path="accueil" element={<PAAccueil slug={slug} />} />
              <Route path="bulletins" element={<PABulletins slug={slug} />} />
              <Route path="bulletin-detail/:bulletinId" element={<PABulletinDetail slug={slug} />} />
              <Route path="justifications" element={<PAJustifications slug={slug} />} />
              <Route path="justifications/:enfantId" element={<PAJustifications slug={slug} />} />
              <Route path="nouvelle-justification/:enfantId" element={<PANouvelleJustification slug={slug} />} />
              <Route path="messages" element={<PAMessages slug={slug} />} />
              <Route path="notifications" element={<PANotifications slug={slug} />} />
              <Route path="abonnements" element={<PAAbonnements slug={slug} />} />
              <Route path="offres" element={<PAOffres slug={slug} />} />
              <Route path="profil" element={<PAProfil slug={slug} />} />
              <Route path="aide" element={<PAAide slug={slug} />} />
              <Route path="contact" element={<PAContact slug={slug} />} />
              <Route path="entrees-sorties" element={<PAEntreesSorties slug={slug} />} />
              <Route path="entrees-sorties/:enfantId" element={<PAEntreesSorties slug={slug} />} />
              <Route path="statistiques/:enfantId" element={<PAStatistiques slug={slug} />} />
              <Route path="situation-frais" element={<PASituationFrais slug={slug} />} />
            </Routes>
          </div>
        </main>
      </div>
    </>
  );
}



// ─── CSS ──────────────────────────────────────────────────────────────
// Thème Kamalpha : bleu profond + or du logo (aligné sur AdminLayout.jsx).
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Cinzel:wght@600;700&display=swap');
@import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  /* Bleu du logo */
  --pr:    #1B4F80;
  --pr-lt: #2F6DA8;
  --pr-dk: #0E2F52;
  --pr-bg: #E6EEF7;

  /* Or du logo (identité uniquement : jamais pour signaler un statut) */
  --or:    #C08A2E;
  --or-lt: #E3B75A;
  --or-dk: #8A5A14;

  --bl: #1a6fff; --bl-bg: #e8f1ff; --bl-dk: #0c447c;
  --tl: #0f6e56; --tl-bg: #e1f5ee;
  --am: #ba7517; --am-bg: #faeeda;
  --rd: #dc3545; --rd-dk: #b02a37; --rd-bg: #fcebeb;
  --gn: #3b6d11; --gn-bg: #eaf3de;
  --s:  #ffffff;
  --s2: #f5f6fa;
  --b:  #e8e8ee;
  --b2: #d0d0da;
  --t:  #0f1b2d;
  --m:  #64748b;
  --h:  #9ba5b4;
  --radius-lg: 10px;
  --radius-md: 7px;
}

body {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: var(--s2);
  font-size: 16px;
  color: var(--t);
  margin: 0;
}

.app { display: flex; flex-direction: column; min-height: 100vh; }

/* ─── TOPBAR PARENT (vide) ─── */
.parent-topbar {
  display: none;
}

/* ─── DRAWER ─── */
.drawer-header {
  background: linear-gradient(135deg, var(--pr-dk), var(--pr));
  padding: 32px 20px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
}

.drawer-header::after {
  content: "";
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--or), transparent);
}

.drawer-logo-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid rgba(192, 138, 46, 0.5);
}

.drawer-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.drawer-brand {
  display: flex;
  flex-direction: column;
}

.drawer-title {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.drawer-subtitle {
  color: var(--or-lt);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.drawer-nav {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
}

.drawer-item-clickable {
  text-decoration: none;
}

.ni-drawer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: var(--t);
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  transition: all 0.2s ease;
  text-decoration: none;
  font-family: inherit;
}

.ni-drawer i {
  font-size: 18px;
  width: 20px;
  flex-shrink: 0;
  color: var(--pr);
}

.ni-drawer.active {
  background: var(--pr-bg);
  color: var(--pr);
  font-weight: 600;
  box-shadow: inset 3px 0 0 var(--or);
}

.ni-drawer.active i {
  color: var(--pr);
}

.ni-drawer:hover {
  background: var(--s2);
}

.drawer-label {
  flex: 1;
}

.drawer-badge {
  background: var(--rd);
  color: #fff;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

.drawer-divider {
  height: 1px;
  background: var(--b);
  margin: 8px 6px;
}

.drawer-logout-wrapper {
  margin: 16px 0 8px;
}

.drawer-logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: none;
  border: 1px solid rgba(220, 53, 69, 0.35);
  cursor: pointer;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: var(--rd);
  transition: background 0.15s;
}

.drawer-logout-btn:hover {
  background: rgba(220, 53, 69, 0.07);
}

.drawer-logout-btn i {
  font-size: 17px;
}

.drawer-version {
  text-align: center;
  padding: 16px 0;
  color: var(--h);
  font-size: 12px;
}

/* ─── MAIN ─── */
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.content { flex: 1; overflow-y: auto; padding: 0; }
.content-full { padding: 0; overflow: hidden; }

/* ─── CLASSES UTILITAIRES ─── */
.ibtn {
  width: 30px; height: 30px; border-radius: 5px;
  border: 0.5px solid var(--b); background: var(--s);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--m); font-size: 17px;
  transition: all .12s;
}
.ibtn:hover { background: var(--s2); color: var(--t); }

.bprim {
  display: flex; align-items: center; gap: 5px;
  padding: 8px 15px; background: var(--pr); color: #fff;
  border: none; border-radius: 5px; font-size: 15px;
  cursor: pointer; font-family: inherit; font-weight: 500;
  white-space: nowrap; transition: opacity .12s;
}
.bprim:hover { opacity: .88; }
.bprim.rd { background: var(--rd); }

.bsec {
  display: flex; align-items: center; gap: 5px;
  padding: 8px 13px; background: var(--s); color: var(--m);
  border: 0.5px solid var(--b2); border-radius: 5px;
  font-size: 15px; cursor: pointer; font-family: inherit;
  transition: all .12s;
}
.bsec:hover { background: var(--s2); color: var(--t); }

.factions { display: flex; gap: 7px; margin-top: 12px; justify-content: flex-end; }

.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.35);
  z-index: 9999;   /* ⬅️ au-dessus du drawer */
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.modal-box {
  background: var(--s); border-radius: var(--radius-lg);
  border: 0.5px solid var(--b); padding: 18px;
  width: 450px; max-width: 100%; max-height: 90vh; overflow-y: auto;
}
.modal-title {
  font-size: 17px; font-weight: 600; color: var(--t);
  margin-bottom: 13px; display: flex; align-items: center; justify-content: space-between;
}

.loader-container { display: flex; justify-content: center; align-items: center; min-height: 300px; width: 100%; }

/* ─── ANIMATIONS ─── */
@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

/* ─── SCROLLBAR ─── */
::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: var(--s2); }
::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: var(--m); }
`;